import { yupResolver } from "@hookform/resolvers/yup";
import { useEffect, useState } from "react";
import { FormProvider, SubmitHandler, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { Button, SectionHeader } from "@pentabd/ui";

import {
  AddNewConfidential,
  AssessmentConfidentialTable,
  BasicInfo,
} from "./components";

import { useACR } from "@hooks/HRM/employee/useACR";

import {
  ADD_CONFIDENTIAL_REPORT,
  acrValidation,
} from "@validations/HRM/employee/acrValidation";

import { AcrType } from "@type/HRM/acr";
import { MASTER_DATA } from "@constants/master-data";
import { useMasterData } from "@hooks/useMasterData";
import { convertDocumentData } from "@utils/documentDataConvert";
import { getAccess } from "@helpers/permissionController";
import { useUser } from "@hooks/useUser";

const { PERFORMANCE_COMMENT, PERFORMANCE_ELIGIBILITY, PERFORMANCE_STATUS } =
  MASTER_DATA;

export interface AcrPropsType {
  id: string | number | null;
  changeActiveTab: (arg: number) => void;
  setIsSuccessModalOpen: (arg: boolean) => void;
  setIsErrorModalOpen: (arg: boolean) => void;
  isProfileDisabled: boolean;
}

function ACR({
  id,
  setIsErrorModalOpen,
  setIsSuccessModalOpen,
  isProfileDisabled,
}: AcrPropsType) {
  const [isEditAcr, setIsEditAcr] = useState<boolean>(true);

  const { t } = useTranslation();
  const {
    createACR,
    createAcrSuccess,
    createAcrFailed,
    acr,
    deleteACRHistory,
    deleteAcrHistorySuccess,
    deleteAcrHistoryFailed,
    createAcrRequestOngoing,
  } = useACR(id);

  const { data: performanceComment } = useMasterData(PERFORMANCE_COMMENT);
  const { data: performanceStatus } = useMasterData(PERFORMANCE_STATUS);
  const { data: performanceEligibility } = useMasterData(
    PERFORMANCE_ELIGIBILITY,
  );

  const { user } = useUser();
  const { realm_roles: roles } = user || {};

  const methods = useForm<AcrType>({
    resolver: yupResolver(acrValidation),
  });

  const { handleSubmit, reset } = methods;

  const onSubmit: SubmitHandler<AcrType> = (data: AcrType) => {
    createACR(convertDocumentData(data, ADD_CONFIDENTIAL_REPORT.UPLOAD_ACR));
  };

  useEffect(() => {
    if (createAcrSuccess) reset();
    setIsSuccessModalOpen(createAcrSuccess || deleteAcrHistorySuccess);
    setIsErrorModalOpen(createAcrFailed || deleteAcrHistoryFailed);

    const editAcrPermission = getAccess(roles, "ACR_UPDATE_PAGE");
    setIsEditAcr(editAcrPermission);
  }, [
    createAcrSuccess,
    createAcrFailed,
    deleteAcrHistorySuccess,
    deleteAcrHistoryFailed,
    methods,
    reset,
  ]);

  return (
    <div className="d-flex flex-column gap-10">
      <div className="pt-10">
        <SectionHeader title={t("EMPLOYEE.ACR_INFO.HEADING_1")} />
      </div>
      <FormProvider {...methods}>
        <form className="container" onSubmit={handleSubmit(onSubmit)}>
          {(isProfileDisabled || isEditAcr) && <BasicInfo />}
          <AssessmentConfidentialTable
            assessmentInfo={acr}
            deleteACRHistory={deleteACRHistory}
            performanceComment={performanceComment}
          />
          {(isProfileDisabled || isEditAcr) && (
            <>
              <AddNewConfidential
                performanceComment={performanceComment}
                performanceStatus={performanceStatus}
                performanceEligibility={performanceEligibility}
              />
              {/* This is the regular submit button */}
              <div className="row py-10">
                <div className="text-end col-12 col-lg-10">
                  <Button
                    key="acr_save"
                    htmlType="submit"
                    fill="outline"
                    type="primary"
                    size="sm"
                    loading={createAcrRequestOngoing}
                  >
                    {t("EMPLOYEE.GENERAL_INFO.FORM.SAVE_BUTTON")}
                  </Button>
                </div>
              </div>
            </>
          )}
        </form>
      </FormProvider>
    </div>
  );
}

export default ACR;
